import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, ArrowUpRight, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { formatDate } from "@/lib/format";

export default function DraftsPanel({ onAccepted, onCount }) {
  const { tr } = useLang();
  const a = tr("admin");
  const [drafts, setDrafts] = useState(null);
  const [topic, setTopic] = useState("");
  const [generating, setGenerating] = useState(false);

  const load = () => {
    api
      .get("/admin/ai/drafts")
      .then((r) => {
        setDrafts(r.data || []);
        onCount((r.data || []).length);
      })
      .catch(() => setDrafts([]));
  };

  useEffect(load, []);

  const generate = async () => {
    setGenerating(true);
    try {
      await api.post("/admin/ai/generate", { topic: topic.trim() || null, section: "insights", category: "INSIGHT" }, { timeout: 240000 });
      toast.success(a.generate);
      setTopic("");
      load();
    } catch {
      toast.error(a.aiError);
    } finally {
      setGenerating(false);
    }
  };

  const accept = async (d) => {
    const { data } = await api.post(`/admin/ai/drafts/${d.id}/accept`);
    toast.success(a.aiAccepted);
    onAccepted(data);
  };

  const discard = async (d) => {
    await api.delete(`/admin/ai/drafts/${d.id}`);
    toast.success(a.aiDiscarded);
    load();
  };

  const btn = "px-4 py-2.5 border font-mono text-[10px] uppercase tracking-[0.15em] transition-colors inline-flex items-center gap-2";

  return (
    <div data-testid="admin-drafts-panel">
      <div className="border border-gold/20 bg-navy-light p-6 lg:p-8">
        <div className="flex items-center gap-3">
          <Sparkles className="h-5 w-5 text-gold" />
          <h2 className="font-serif text-2xl text-ivory">{a.aiTitle}</h2>
        </div>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ivory/55">{a.aiSub}</p>
        <div className="mt-6 flex flex-col gap-3 sm:flex-row">
          <input
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={a.topicPh}
            data-testid="ai-topic-input"
            className="w-full border border-white/15 bg-white/5 px-4 py-3.5 text-sm text-ivory placeholder:text-ivory/30 transition-colors focus:border-gold/60 focus:outline-none"
          />
          <button
            onClick={generate}
            disabled={generating}
            data-testid="ai-generate-draft-button"
            className="inline-flex shrink-0 items-center justify-center gap-2 bg-gold px-6 py-3.5 text-xs font-semibold uppercase tracking-[0.18em] text-navy transition-colors hover:bg-gold-bright disabled:pointer-events-none disabled:opacity-60"
          >
            <Sparkles className="h-4 w-4" />
            {generating ? a.generating : a.generate}
          </button>
        </div>
      </div>

      {drafts === null ? (
        <p className="py-16 text-center font-mono text-xs uppercase tracking-[0.25em] text-ivory/40">…</p>
      ) : drafts.length === 0 && !generating ? (
        <p className="mt-8 border border-gold/15 py-14 text-center text-sm text-ivory/50" data-testid="ai-drafts-empty">
          {a.aiEmpty}
        </p>
      ) : (
        <div className="mt-8 space-y-5" data-testid="ai-drafts-list">
          {(drafts || []).map((d) => (
            <div key={d.id} data-testid={`ai-draft-${d.id}`} className="border border-white/10 bg-white/[0.02] p-6">
              <div className="flex flex-wrap items-center gap-3">
                <span className="border border-gold/30 px-2.5 py-1 font-mono text-[10px] uppercase tracking-[0.2em] text-gold-bright">{d.category}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-ivory/40">
                  {d.section === "knowledge" ? "Knowledge" : "Insights"} · {formatDate(d.created_at, "it")}
                </span>
              </div>
              <div className="mt-4 grid gap-6 md:grid-cols-2">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">IT</p>
                  <h3 className="mt-2 font-serif text-lg text-ivory">{d.it?.title}</h3>
                  <p className="mt-2 text-sm text-ivory/55">{d.it?.excerpt}</p>
                </div>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold">EN</p>
                  <h3 className="mt-2 font-serif text-lg text-ivory">{d.en?.title}</h3>
                  <p className="mt-2 text-sm text-ivory/55">{d.en?.excerpt}</p>
                </div>
              </div>
              <p className="mt-4 max-h-28 overflow-y-auto border-t border-white/10 pt-3 text-xs leading-relaxed text-ivory/40">
                {(d.it?.body || "").slice(0, 400)}…
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <button onClick={() => accept(d)} data-testid={`ai-accept-${d.id}`} className={`${btn} border-gold/50 text-gold-bright hover:bg-gold hover:text-navy`}>
                  <ArrowUpRight className="h-3.5 w-3.5" /> {a.aiUse}
                </button>
                <button onClick={() => discard(d)} data-testid={`ai-discard-${d.id}`} className={`${btn} border-red-400/30 text-red-300 hover:bg-red-500/20`}>
                  <Trash2 className="h-3.5 w-3.5" /> {a.aiDiscard}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
